import { Environment } from "shared/core/Environment";
import { RequestClass } from "./core/olga/RequestClass";

export class ReportsAPI {
  constructor() {
    this.client = new RequestClass(
      process.env.REACT_APP_SERVICES_ENDPOINT_BASE
    );
    this.token = Environment.token;
    this.environment_id = Environment.id;
  }

  async orders(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/orders/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });

    return response.getData({
      statistics: {},
      periods: [],
      distribution: {
        units: [],
        brands: [],
        origins: [],
      },
    });
  }

  async customerGroups() {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/customers/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({
      statistics: {},
      groups: [],
      transitions: {},
    });
  }

  async customerTransitions(year, month) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/customers/${this.environment_id}/transitions/${year}/${month}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async customerHistory(type, year, month) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/customers/${this.environment_id}/history/${type}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        year,
        ...(month && { month }),
      },
    });
    return response.getData([]);
  }

  async rfv(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/rfv/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });

    return response.getData({});
  }

  async rfvDetails(year, month, brand_id = null, unit_id = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/rfv/${this.environment_id}/details/${year}/${month}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...(brand_id && { brand_id }),
        ...(unit_id && { unit_id }),
      },
    });
    return response.getData({});
  }

  async rfvTransitions(year, month, brand_id = null, unit_id = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/rfv/${this.environment_id}/transitions/${year}/${month}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...(brand_id && { brand_id }),
        ...(unit_id && { unit_id }),
      },
    });
    return response.getData({});
  }

  async rfvHistory(type, year, month, brand_id = null, unit_id = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/rfv/${this.environment_id}/history/${type}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        year,
        ...(month && { month }),
        ...(brand_id && { brand_id }),
        ...(unit_id && { unit_id }),
      },
    });
    return response.getData({});
  }

  async lifeCycle(type, year, month) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/lifecycle/${this.environment_id}/${type}/${year}/${month}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async lifeCycleHistory(type, year, month) {
    let endpoint = `/lifecycle/${this.environment_id}/history/${type}`;
    if (year) endpoint += `/${year}`;
    if (month) endpoint += `/${month}`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async flow(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowCampaignsDetails(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows/campaigns`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowCampaign(flowId, filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows/campaigns/${flowId}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowCreative(flowId, creativeId, filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows/campaigns/${flowId}/creatives/${creativeId}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowCoupon(flowId, creativeId, couponId, filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows/campaigns/${flowId}/creatives/${creativeId}/coupons/${couponId}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowCouponUses(flowId, creativeId, couponId, filters) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/flow/${this.environment_id}/flows/campaigns/${flowId}/creatives/${creativeId}/coupons/${couponId}/uses`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async flowHistory(type, year, month) {
    let endpoint = `/flow/${this.environment_id}/flows/history/${type}`;
    if (year) endpoint += `/${year}`;
    if (month) endpoint += `/${month}`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async coupons(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/coupons/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async couponsType(type, { page = 1, page_size = 10 } = {}, filters) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/coupons/${this.environment_id}/${type}?page=${page}&page_size=${page_size}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async loyaltyIndicators(filters = null) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/indicators/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async loyaltyIndicatorsHistory(year, month) {
    let endpoint = `/indicators/${this.environment_id}/history/keynumbers`;
    if (year) endpoint += `/${year}`;
    if (month) endpoint += `/${month}`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async loyaltyEngagements() {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/engagements/${this.environment_id}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async loyaltyEngagementsHistory(type, year) {
    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint: `/engagements/${this.environment_id}/history/${type}/${year}`,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }

  async loyaltyOperations(
    type,
    filters = null,
    { page = 1, page_size = 10 } = {}
  ) {
    let endpoint = `/operations/${this.environment_id}`;
    if (type) endpoint += `/${type}?page=${page}&page_size=${page_size}`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async loyaltyCoupons(type, filters = null, params) {
    let endpoint = `/loyalty-coupons/${this.environment_id}`;
    if (type) endpoint += `/${type}`;
    if (params)
      endpoint += `?page=${params.page}&page_size=${params.page_size}`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      params: {
        ...filters,
      },
    });
    return response.getData({});
  }

  async getAllCoupons() {
    let endpoint = `/coupons/${this.environment_id}/list`;

    const response = await this.client.get({
      ignoreVersion: true,
      service: "reports",
      endpoint,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return response.getData({});
  }
}
